import {createApp} from 'vue'
import {createStore} from 'vuex'
import App from './App.vue'
import {/*InteractionRequiredAuthError, */LogLevel, PublicClientApplication} from '@azure/msal-browser'

function getWorkday(day, hours = 0, minutes = 0, seconds = 0) {
    const date = new Date();
    const today = date.getDate();
    const dayOfTheWeek = date.getDay() || 7;
    const weekendCompensation = 2 * Math.floor((day + (dayOfTheWeek < 6 ? dayOfTheWeek - 1 : 0)) / 5)
        + (dayOfTheWeek > 5 ? 8 - dayOfTheWeek : 0)
    const newDate = date.setDate(today + day + weekendCompensation);
    const finalDate = new Date(newDate);
    finalDate.setHours(hours)
    finalDate.setMinutes(minutes)
    finalDate.setSeconds(seconds)
    finalDate.setMilliseconds(0)
    return finalDate;
}

const store = createStore({
    state() {
        return {
            client: null,
            clientId: 'c4594704-cd87-4976-8818-e3b37414fccb',
            clientHeaders: {},
            title: "<strong>Xsarus</strong> Rooster",
            users: [],
            groups: [],
            activeGroup: null,
            activeGroupId: null,
            workDays: 10,
            dateWindow: [],
            schedule: new Map(),
            autoRefresh: false,
            search: null
        }
    },
    getters: {
        client(state) {
            return state.client
        },
        clientId(state) {
            return state.clientId
        },
        clientHeaders(state) {
            return state.clientHeaders
        },
        title(state) {
            return state.title
        },
        users(state) {
            return state.users
        },
        groups(state) {
            return state.groups
        },
        activeGroup(state) {
            return state.activeGroup
        },
        workDays(state) {
            return state.workDays
        },
        dateWindow(state) {
            return state.dateWindow
        },
        schedule(state) {
            return state.schedule
        },
        autoRefresh(state) {
            return state.autoRefresh
        },
        search(state) {
            return state.search
        }
    },
    actions: {
        initClient({commit, getters}) {
            commit('client', new PublicClientApplication({
                auth: {
                    clientId: getters.clientId,
                    authority: "https://login.microsoftonline.com/organizations/",
                    redirectUri: window.location.href
                },
                cache: {
                    cacheLocation: "localStorage", // This configures where your cache will be stored
                    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
                },
                system: {
                    loggerOptions: {
                        loggerCallback: (level, message, containsPii) => {
                            if (containsPii) {
                                return;
                            }
                            switch (level) {
                                case LogLevel.Error:
                                    console.error(message);
                                    return;
                                case LogLevel.Info:
                                    console.info(message);
                                    return;
                                case LogLevel.Verbose:
                                    console.debug(message);
                                    return;
                                case LogLevel.Warning:
                                    console.warn(message);
                                    return;
                            }
                        }
                    }
                }
            }))
        },
        async login({getters}, request) {
            return await getters.client.loginPopup(request).then(
                (response) => {
                    getters.client.setActiveAccount(response.account);
                }
            )
        },
        async updateClientHeaders({getters, commit}) {
            const currentAcc = getters.client.getActiveAccount();
            if (currentAcc) {
                let token = await getters.client.acquireTokenSilent(currentAcc)
                let accessToken = token.accessToken
                const headers = new Headers();
                const bearer = `Bearer ${accessToken}`;
                headers.append("Authorization", bearer);
                headers.append("Content-Type", "application/json");
                commit('clientHeaders', headers)
            }
        },
        async loadScheduleForDate({getters, dispatch}, date, loadHeaders = false) {
            const currentAcc = getters.client.getActiveAccount();
            if (currentAcc) {
                if (loadHeaders) {
                    await dispatch('updateClientHeaders')
                }
                let url = new URL('https://graph.microsoft.com/v1.0/me/calendar/getSchedule')
                let startTime = new Date()
                startTime.setDate(date.getDate())
                startTime.setTime(date.getTime())
                let endTime = new Date()
                endTime.setDate(date.getDate())
                endTime.setTime(date.getTime())
                endTime.setMinutes(5)

                let users = getters.users
                if (getters.activeGroup) {
                    let groupMails = getters.activeGroup.members.map((member) => member.mail)
                    users = users.filter((user) => groupMails.includes(user.email))
                }

                getters.schedule.set(
                    endTime.toLocaleDateString(
                        'nl-NL',
                        {weekday: 'short', month: 'numeric', day: 'numeric'}
                    ),
                    new Map(users.map((user) => [user.email, {loading: true,}]))
                )

                await fetch(url, {
                    method: "POST",
                    headers: getters.clientHeaders,
                    body: JSON.stringify({
                        "schedules": users.map((user) => user.email),
                        "startTime": {
                            "dateTime": startTime.toISOString(),
                            "timeZone": "UTC"
                        },
                        "endTime": {
                            "dateTime": endTime.toISOString(),
                            "timeZone": "UTC"
                        },
                        "availabilityViewInterval": 5
                    })
                })
                    .then(response => response.json())
                    .then(response => {
                        console.log(response)
                        let scheduleMap = new Map()
                        for (let i = 0; i < response.value.length; i++) {
                            scheduleMap.set(
                                response.value[i].scheduleId,
                                {
                                    loading: false,
                                    scheduleItems: response.value[i].scheduleItems
                                }
                            )
                        }
                        getters.schedule.set(
                            endTime.toLocaleDateString(
                                'nl-NL',
                                {weekday: 'short', month: 'numeric', day: 'numeric'}
                            ),
                            scheduleMap
                        )
                    })
                    .catch(error => console.log(error));
            }
        },
        async loadSchedule({getters, dispatch}) {
            const currentAcc = getters.client.getActiveAccount();
            if (currentAcc) {
                await dispatch('updateClientHeaders')
                for (let i = 0; i < getters.dateWindow.length; i++) {
                    await dispatch('loadScheduleForDate', getters.dateWindow[i])
                    await new Promise(r => setTimeout(r, 1000));
                }
            }
        },
        async loadGroups({commit, getters, dispatch}) {
            const currentAcc = getters.client.getActiveAccount();
            if (currentAcc) {
                await dispatch('updateClientHeaders')
                let url = new URL('https://graph.microsoft.com/v1.0/groups?$filter=startswith(mail, \'team.\')&$select=id,displayName&$expand=members($select=id,mail,displayName,accountEnabled)')
                await fetch(url, {
                    method: "GET",
                    headers: getters.clientHeaders,
                })
                    .then(response => response.json())
                    .then(response => {
                        commit('groups', response.value)
                        commit('users', [
                            ...new Map(
                                response.value.reduce(
                                    (users, group) => [
                                        ...users,
                                        ...group.members.filter((member) => member.accountEnabled).map((member) => {return {email: member.mail, name: member.displayName}})
                                    ],
                                    []
                                ).map(item => [item.email, item])
                            ).values()
                        ])
                    })
            }
        },
        calculateDateWindow({commit, getters}) {
            let dateWindow = []
            for (let workDay = 0; workDay < getters.workDays; workDay++) {
                dateWindow.push(getWorkday(workDay, 3, 0, 0))
            }
            commit('dateWindow', dateWindow)
            for (const key of getters.schedule.keys()) {
                if (dateWindow.filter((date) => date.toLocaleDateString(
                    'nl-NL',
                    {weekday: 'short', month: 'numeric', day: 'numeric'}
                ) === key).length === 0) {
                    getters.schedule.delete(key)
                }
            }
        }
    },
    mutations: {
        initialiseStore(state) {
            console.log('init store')
            if (localStorage.getItem('title')) {
                state.title = localStorage.getItem('title');
            }
            if (localStorage.getItem('workDays')) {
                state.workDays = parseInt(localStorage.getItem('workDays'));
            }
            if (localStorage.getItem('autoRefresh')) {
                state.autoRefresh = JSON.parse(localStorage.getItem('autoRefresh'));
            }
            if (localStorage.getItem('activeGroupId')) {
                state.activeGroupId = localStorage.getItem('activeGroupId');
            }
        },
        client(state, client) {
            state.client = client
        },
        clientHeaders(state, clientHeaders) {
            state.clientHeaders = clientHeaders
        },
        users(state, users) {
            state.users = users
            state.users.sort((a, b) => a.name.localeCompare(b.name))
        },
        groups(state, groups) {
            state.groups = groups
            if (state.activeGroupId) {
                state.activeGroup = groups.find((group) => group.id === state.activeGroupId)
            }
        },
        activeGroup(state, activeGroup) {
            state.activeGroup = activeGroup
            state.activeGroupId = activeGroup?.id
            localStorage.setItem('activeGroupId', activeGroup?.id);
        },
        title(state, title) {
            state.title = title
            localStorage.setItem('title', title);
        },
        workDays(state, workDays) {
            state.workDays = parseInt(workDays)
            localStorage.setItem('workDays', workDays);
        },
        dateWindow(state, dateWindow) {
            state.dateWindow = dateWindow
        },
        autoRefresh(state, autoRefresh) {
            state.autoRefresh = autoRefresh
            localStorage.setItem('autoRefresh', JSON.stringify(state.autoRefresh));
        },
        search(state, search) {
            state.search = search
        }
    }
})

const app = createApp(App)
app.use(store)
app.mount('#app')



